import styled from 'styled-components'

export const PageSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  max-width: 640px;
  line-height: 1.45;
`

export const PageBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
`

export const SwitchCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const SwitchLabelText = styled.span<{ $on?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme, $on }) => ($on ? theme.dashboard.infoDeep : theme.colors.iconGray)};
  white-space: nowrap;
`

export const AnchorTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
`

export const AnchorHint = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

export const ReminderCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

export const MessagePreviewBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  > div {
    margin-bottom: 0;
  }
`

export const PreviewRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

export const ReminderCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

export const Eyebrow = styled.span<{ $tone?: 'info' | 'collection' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme, $tone }) =>
    $tone === 'collection'
      ? theme.dashboard.caution
      : $tone === 'muted'
      ? theme.colors.iconGray
      : theme.dashboard.infoDeep};
`

export const ReminderMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

export const DaysChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.colors.neutralGray};
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: 8px;
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.menuBg};
`

export const DaysInput = styled.input`
  width: 2.4rem;
  border: none;
  background: transparent;
  font: inherit;
  font-weight: 700;
  color: inherit;
  padding: 0;
  outline: none;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  appearance: textfield;
`

export const DaysUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

export const MetaHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

export const ReminderDaysRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;

  ${MetaHint} {
    white-space: nowrap;
  }
`

export const FieldError = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.error};
`

export const ReminderEmailNote = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  line-height: 1.45;
`

export const SubjectBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const SubjectLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.metalGray};
`

export const SubjectValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;
`

export const AddStepRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding-bottom: ${({ theme }) => theme.spacing.xl}rem;
`

export const EmptyReminders = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
`

export const CollectionCard = styled.div<{ $dashed?: boolean }>`
  border: 1px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')}
    ${({ theme, $dashed }) => ($dashed ? theme.colors.borderStrong : theme.colors.borderSoft)};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: ${({ $dashed }) => ($dashed ? '14px' : '0')};
  padding: ${({ $dashed }) => ($dashed ? '16px' : '0')};
`

export const CollectionHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

export const CollectionHeaderCopy = styled.div`
  flex: 1;
  min-width: 0;
`

export const HandoverSwitch = styled(SwitchCluster)`
  margin-top: 12px;
`

export const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

export const OutlineBadge = styled.span<{ $tone?: 'neutral' | 'warning' }>`
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'warning' ? theme.colors.warning : theme.colors.borderStrong};
  color: ${({ theme, $tone }) =>
    $tone === 'warning' ? theme.dashboard.caution : theme.dashboard.textSubtle};
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 600;
  white-space: nowrap;
`

export const CollectionTitle = styled.h3`
  margin: 6px 0 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

export const CollectionDescription = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.45;
  margin-top: 4px;
`

export const CollectionBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

export const InfoRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.6;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  padding-top: 14px;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }

  svg {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSize.px14}rem;
    margin-top: 3px;
    flex-shrink: 0;
  }
`

export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  flex-wrap: wrap;

  a {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`

export const SavePrompt = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

export const SavePromptGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

export const SavePromptItem = styled.div<{ $tone: 'apply' | 'skip' }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: 10px ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${({ theme, $tone }) =>
      $tone === 'apply' ? theme.colors.success : theme.colors.iconGray};
  }
`
